import type { Platform } from "@/platform/Platform";

/**
 * The full typed analytics event catalog - every event name, its
 * property shape, and the single schema version stamped onto all of
 * them. This is the only place PostHog event names/property names are
 * spelled out; every call site (scenes, `gameAnalytics.ts`) goes through
 * `trackEvent()` below, which is generic over this map, so a typo or a
 * missing/extra property is a compile error, not a silent bad payload in
 * PostHog Live Events.
 *
 * Deliberately holds no PostHog-specific code or imports - see
 * `posthogClient.ts` for the only file in this project allowed to import
 * `posthog-js`.
 */

/** Bumped whenever an event's property shape changes in a way analysis needs to know about - stamped onto every event as `analytics_version`. */
export const ANALYTICS_VERSION = 1;

interface BaseEventProps {
  analytics_version: typeof ANALYTICS_VERSION;
}

export type Stars = 0 | 1 | 2 | 3;
export type InputMode = "keyboard" | "touch";
/**
 * Which game mode a gameplay event happened in. Centralized here as the
 * single source of truth so a future mode (Daily Challenge, Endless) is
 * only ever a new member of this union - every event property/payload
 * builder that carries `game_mode` is typed against it, so adding a mode
 * is a compile-time-checked change, not a free-text string threaded
 * through by hand. Only `"campaign"` is actually reachable today; see
 * `CURRENT_GAME_MODE`.
 */
export type GameMode = "campaign" | "daily" | "endless";
/** The campaign's own fixed `game_mode` - every campaign call site passes this literal. Daily Challenge and Endless call sites pass `"daily"`/`"endless"` explicitly instead (see `GameScene`'s `gameMode` field). */
export const CURRENT_GAME_MODE: GameMode = "campaign";

/**
 * Daily Challenge context, threaded through every gameplay event
 * alongside `game_mode: "daily"` - entirely optional (`undefined` for
 * every campaign event) so no existing event/call site changes shape.
 * Deliberately excludes anything PII/identity-shaped: no participant/
 * device id, no display name, no leaderboard rank - see the PR
 * description's analytics section.
 */
export interface DailyAnalyticsProps {
  challenge_id?: string;
  generator_version?: number;
  attempt_number?: number;
  attempts_remaining?: number;
  /** Stable layout archetype id for a V2 date (e.g. "hourglass_pinch"), or the legacy `mapFamily` for an already-stored V1 date - see `DailyChallengeSpecAny`. Absent for a campaign run. */
  layout_id?: string;
  /** V2 only - the day's Daily Points scoring profile id (e.g. "survival_heavy"). Absent for a campaign run or a V1 date (no scoring profile). */
  scoring_profile_id?: string;
}

/**
 * `"quit"` and `"arena_cleared"` are Endless Growth V1 only - a voluntary
 * END RUN and the exceptional full-arena completion, respectively. Never
 * produced by Campaign/Daily, whose own outcomes (`"completed"`,
 * `"restart"`, `"menu_exit"`) are untouched by this addition.
 */
export type RunFinishedOutcome = "completed" | "death" | "restart" | "menu_exit" | "quit" | "arena_cleared";
export type AnalyticsDeathReason = "wall" | "obstacle" | "self";

/**
 * Instant Play & Onboarding V1: an analytics-only classification of how a
 * run actually started - deliberately NEVER the backend-shared `RunSource`
 * type in `src/online/types.ts` (that type is a Supabase CHECK-constraint
 * contract for Campaign leaderboard submission - `run_source in ('play',
 * 'replay', 'next_level', 'level_select')` - and must never gain a value
 * the database/RPC doesn't already accept). `"menu"` and `"instant_play"`
 * both correspond to the backend's `"play"` - this is the one place that
 * split is visible, purely for analysis.
 */
export type AnalyticsRunSource = "instant_play" | "menu" | "replay" | "level_select" | "next_level";

/** Which result screen a worlds-discovery event happened on. */
export type ResultSource = "instant_play" | "endless_menu" | "daily" | "beat_my_score";

export interface TutorialCompletedProps extends BaseEventProps {
  tutorial_version: number;
  /** Omitted whenever the actual input device can't be reliably known - see the PR description. */
  input_mode?: InputMode;
}

export interface RunStartedProps extends BaseEventProps, DailyAnalyticsProps {
  run_id: string;
  /** PR34: the run's frozen Mobile/Desktop category (`detectPlatform()` at run start) - the same value its leaderboard submission carries. */
  platform?: Platform;
  world_id: string;
  level_id: string;
  game_mode: GameMode;
  starting_length: number;
  /** Every campaign run's real target length. Absent (never a fabricated/sentinel number) for a Daily survival run, which has no target length at all - see the PR description's "must model survival without fabricating target_length" rule. */
  target_length?: number;
  is_replay: boolean;
  previous_best_score: number;
  previous_best_stars: Stars;
  /** Instant Play & Onboarding V1 - see `AnalyticsRunSource`'s own doc. Omitted for a Daily/QA-preview run, which has no equivalent concept. */
  run_source?: AnalyticsRunSource;
}

export interface GateOpenedProps extends BaseEventProps, DailyAnalyticsProps {
  run_id: string;
  world_id: string;
  level_id: string;
  game_mode: GameMode;
  score: number;
  length: number;
  elapsed_ms: number;
  steps?: number;
}

export interface RunFinishedProps extends BaseEventProps, DailyAnalyticsProps {
  run_id: string;
  /** PR34: the run's frozen Mobile/Desktop category (`detectPlatform()` at run start) - the same value its leaderboard submission carries. */
  platform?: Platform;
  world_id: string;
  level_id: string;
  game_mode: GameMode;
  outcome: RunFinishedOutcome;
  /** Only ever present for `outcome: "death"` - see the PR description for why it's never guessed for any other outcome. */
  death_reason?: AnalyticsDeathReason;
  score: number;
  final_length: number;
  elapsed_ms: number;
  active_play_ms: number;
  steps: number;
  /** Only ever present for `outcome: "completed"`. */
  stars_earned?: Stars;
  /** The resulting best score/stars for this level *after* this run - not a boolean; see `best_score_improved`/`best_stars_improved` for the flag. */
  new_best_score: number;
  new_best_stars: Stars;
  best_score_improved: boolean;
  best_stars_improved: boolean;
  safe_bites: number;
  rush_bites: number;
  chaos_bites: number;
  max_rush_multiplier: number;
  /** Endless Growth V1 only - the fastest (smallest) movement interval actually reached this run, in ms. Absent for every Campaign/Daily run. */
  fastest_step_ms?: number;
  /** Endless Growth V1 only - whether this run's canonical local best record actually improved (see `EndlessStorage`'s comparator). Deliberately a separate field from `best_score_improved`/`best_stars_improved`, which are Campaign's own star/high-score concepts and never meaningful for Endless. Absent for every Campaign/Daily run. */
  endless_new_best?: boolean;
  /** Instant Play & Onboarding V1 - see `AnalyticsRunSource`'s own doc. Omitted for a Daily/QA-preview run, which has no equivalent concept. */
  run_source?: AnalyticsRunSource;
}

/**
 * Endless global leaderboard / submission lifecycle events (PR: Endless
 * Global Leaderboard V1). None of these ever carry a player id, display
 * name, raw JWT, the full submitted payload, or an individual leaderboard
 * row - `rank_bucket` is always one of a small fixed set of coarse
 * strings, never an exact rank number that could be combined with other
 * signals to re-identify a specific row. Deliberately never duplicates the
 * existing gameplay events (`run_started`/`run_finished`/etc., already
 * wired for `game_mode: "endless"`) - these are leaderboard/submission-
 * specific only.
 */
export type EndlessLeaderboardOpenSource = "menu" | "result_screen";
/** Never the exact rank - see this block's own doc. */
export type EndlessRankBucket = "top_10" | "top_100" | "outside_top_100" | "unranked";
export type EndlessAnalyticsEndReason = "death" | "quit" | "arena_cleared";
/** `"validation"` - a definitive, permanently-rejected (4xx) submission, never retried. `"server_error"` is intentionally not distinguished from a network failure - both are `endless_submission_failed` (still queued, will retry). */
export type EndlessSubmissionRejectionReason = "validation";

export interface EndlessLeaderboardOpenedProps extends BaseEventProps {
  source: EndlessLeaderboardOpenSource;
}
export interface EndlessSubmissionAttemptedProps extends BaseEventProps {
  end_reason: EndlessAnalyticsEndReason;
}
export interface EndlessSubmissionAcceptedProps extends BaseEventProps {
  end_reason: EndlessAnalyticsEndReason;
  is_new_best: boolean;
  rank_bucket: EndlessRankBucket;
}
export interface EndlessSubmissionRejectedProps extends BaseEventProps {
  reason: EndlessSubmissionRejectionReason;
}
/** A transient failure (network unreachable, 5xx) - the run stays safely queued for a later retry, never dropped. */
export type EndlessSubmissionFailedProps = BaseEventProps;
export interface EndlessNewOnlineBestProps extends BaseEventProps {
  rank_bucket: EndlessRankBucket;
}

/** Fired when the Daily Challenge pre-run screen loads with a resolved challenge - before any attempt is reserved, so it never carries an `attempt_number`. */
export interface DailyChallengeOpenedProps extends BaseEventProps {
  challenge_id: string;
  generator_version: number;
  bite_mode: "standard" | "safe_only" | "rush_only" | "chaos_only";
  /** V2's layout archetype id, or a V1 date's legacy `mapFamily` - see `DailyChallengeSpecAny`. */
  layout_id: string;
  /** V2 only - absent for a V1 date (no scoring profile). */
  scoring_profile_id?: string;
  attempts_remaining: number;
}

/**
 * "Beat My Score" (PR10) sharing/challenge event property shapes. None of
 * these ever carry a challenge token, a full URL, a player/creator id, an
 * email, or a display name - see the PR description's "Do not send"
 * list. `margin_bucket`/`attempts_remaining_bucket` are always one of a
 * small fixed set of coarse strings (`src/daily/scoreChallenge.ts`), never
 * an exact number that combined with a public target could reconstruct a
 * raw score.
 */
export type ShareContext = "own_result" | "reshare";
export type ShareMethod = "native_share" | "clipboard";
export type ScoreChallengeStatusProp = "active" | "expired" | "invalid";
export type ScoreOutcome = "beat" | "tie" | "lost";
export type MarginBucket = "0" | "1-10" | "11-50" | "51-200" | "201-500" | "500+";
export type AttemptsRemainingBucket = "0" | "1" | "2" | "3";

/** Fired the moment the CHALLENGE A FRIEND affordance becomes visible/available - never before a ranked result is server-verified (see the PR description's "Share button unavailable until accepted" rule). */
export interface ScoreSharePromptViewedProps extends BaseEventProps {
  context: ShareContext;
}
export interface ScoreShareRequestedProps extends BaseEventProps {
  context: ShareContext;
}
export interface ScoreShareSucceededProps extends BaseEventProps {
  context: ShareContext;
  method: ShareMethod;
}
/** Only ever fires for `method: "native_share"` - a clipboard fallback has no user-cancellable prompt. */
export interface ScoreShareCancelledProps extends BaseEventProps {
  context: ShareContext;
}
export interface ScoreShareFailedProps extends BaseEventProps {
  context: ShareContext;
  method: ShareMethod;
}
export interface ScoreShareLinkCopiedProps extends BaseEventProps {
  context: ShareContext;
}
/** Fired when a challenge landing page resolves - `self_referral: true` (the creator opening their own link) is deliberately excluded from conversion-metric queries downstream, not from this event itself (see the PR description's "exclude the visit from referral conversion metrics" - the raw event is still useful for total-opens debugging). Never fired for a social-preview crawler, which never executes this client-side code at all. */
export interface ScoreChallengeOpenedProps extends BaseEventProps {
  status: ScoreChallengeStatusProp;
  self_referral: boolean;
  returning_player: boolean;
}
export interface ScoreChallengeCtaClickedProps extends BaseEventProps {
  attempts_remaining_bucket: AttemptsRemainingBucket;
}
export interface ScoreChallengeRunStartedProps extends BaseEventProps {
  attempts_remaining_bucket: AttemptsRemainingBucket;
  generator_version: number;
}
export interface ScoreChallengeVerifiedResultProps extends BaseEventProps {
  outcome: ScoreOutcome;
  margin_bucket: MarginBucket;
  generator_version: number;
}
/** The extra marker distinguishing "a challenge participant is now sharing their own result" from a first-time, unattributed share - see `score_share_*`'s own doc for the always-fired general share funnel this supplements. */
export interface ScoreChallengeResharedProps extends BaseEventProps {
  outcome: ScoreOutcome;
}

// ---------------------------------------------------------------------
// Instant Play & Onboarding V1 - entry funnel, movement onboarding, and
// worlds-discovery events. None of these ever carry a player id, display
// name, or raw score - see each interface's own doc.
// ---------------------------------------------------------------------

/** Fired the instant PLAY NOW is activated (pointer, Enter, or Space) - before the audio-unlock call or the scene transition, so it always fires even if either of those somehow fails. Guarded against double-activation by the same click/keypress debounce that starts the run itself - see `EntryScene`. */
export type PlayNowClickedProps = BaseEventProps;
/** Fired the moment the pre-run "PRESS A DIRECTION TO START"/"SWIPE TO START" ready gate actually clears - the Instant Play conversion funnel's "first valid input" step. Never a tutorial-completion event - there is no separate movement tutorial. */
export interface FirstInputReceivedProps extends BaseEventProps {
  game_mode: GameMode;
  input_mode: InputMode;
}

/**
 * Distinguishes the current Endless result-screen treatment (a normal,
 * persistent part of every result - see `EndlessResultScene`) from the
 * historical one-time promotion Daily/Beat My Score still use. Omitted
 * entirely for Daily/Beat My Score's own `worlds_discovery_clicked` call,
 * which is unchanged by this addition - only `EndlessResultScene`'s call
 * site passes it.
 */
export type DiscoveryVariant = "persistent_result_cta";

export interface WorldsDiscoveryClickedProps extends BaseEventProps {
  result_source: ResultSource;
  discovery_variant?: DiscoveryVariant;
}

// ---------------------------------------------------------------------
// PR28: minimal client-side error observability. `client_error` is the
// ONLY event either of these two producers fires - see
// `src/analytics/errorReporting.ts`, the sole call site.
// ---------------------------------------------------------------------

export type ClientErrorSource = "window_error" | "unhandled_rejection";

/**
 * `message`/`stack` are always already sanitized/truncated by
 * `errorReporting.ts` before this ever reaches `trackEvent()` - never a
 * raw, unbounded error object, never an access token/auth header/cookie/
 * localStorage value, and never a full query string (which could carry a
 * Beat My Score challenge token) - see that module's own doc. `path` is
 * always `location.pathname` alone, never `.search`/`.hash`/`.href`, for
 * the same reason.
 */
export interface ClientErrorProps extends BaseEventProps {
  source: ClientErrorSource;
  message: string;
  stack?: string;
  path: string;
}

/**
 * PR28: Daily's own equivalent of `endless_submission_failed` - fired
 * only when an actual `daily-result` submission attempt fails (offline or
 * a network/server error), never for a success. Carries no score/length/
 * duration/steps - this is a signal that a result may have been lost, not
 * a copy of it (the real value is already gone if the player never
 * presses RETRY SAVE - see `DailyResultScene`).
 */
export type DailySubmissionFailureCategory = "offline" | "error";
export interface DailySubmissionFailedProps extends BaseEventProps {
  challenge_id: string;
  generator_version: number;
  attempt_number: number;
  failure_category: DailySubmissionFailureCategory;
  /** Whether RETRY SAVE is offered for this failure - always `true` today (both `"offline"` and `"error"` show it, see `DailyResultScene`'s own render logic) - kept explicit so a future non-retryable failure state is distinguishable downstream rather than looking identical to this one. */
  retryable: boolean;
}

// ---------------------------------------------------------------------
// PR33 (Launch & Business Readiness): a dedicated, explicit "deliberate
// replay" signal - RETRY/REPLAY (Campaign), PLAY AGAIN (Endless), and TRY
// AGAIN (Daily) all fire this the moment they're tapped, on the result
// screen of an already-finished run. `run_started`'s own `run_source:
// "replay"` already exists for a different purpose (crediting where a
// run's own stats came from) and isn't a reliable stand-in for this: it
// requires correlating two separate anonymous events across a session
// with no shared run/player id, and never fires at all for Daily's TRY
// AGAIN (which starts `DailyChallengeScene`, not a run, until an attempt
// is actually reserved). This event exists purely to answer "what
// percentage of finished runs lead to a deliberate replay" directly.
// ---------------------------------------------------------------------

export interface PlayAgainClickedProps extends BaseEventProps {
  game_mode: GameMode;
}

export interface AnalyticsEventMap {
  tutorial_completed: TutorialCompletedProps;
  run_started: RunStartedProps;
  gate_opened: GateOpenedProps;
  run_finished: RunFinishedProps;
  daily_challenge_opened: DailyChallengeOpenedProps;
  endless_leaderboard_opened: EndlessLeaderboardOpenedProps;
  endless_submission_attempted: EndlessSubmissionAttemptedProps;
  endless_submission_accepted: EndlessSubmissionAcceptedProps;
  endless_submission_rejected: EndlessSubmissionRejectedProps;
  endless_submission_failed: EndlessSubmissionFailedProps;
  endless_new_online_best: EndlessNewOnlineBestProps;
  score_share_prompt_viewed: ScoreSharePromptViewedProps;
  score_share_requested: ScoreShareRequestedProps;
  score_share_succeeded: ScoreShareSucceededProps;
  score_share_cancelled: ScoreShareCancelledProps;
  score_share_failed: ScoreShareFailedProps;
  score_share_link_copied: ScoreShareLinkCopiedProps;
  score_challenge_opened: ScoreChallengeOpenedProps;
  score_challenge_cta_clicked: ScoreChallengeCtaClickedProps;
  score_challenge_run_started: ScoreChallengeRunStartedProps;
  score_challenge_verified_result: ScoreChallengeVerifiedResultProps;
  score_challenge_reshared: ScoreChallengeResharedProps;
  play_now_clicked: PlayNowClickedProps;
  first_input_received: FirstInputReceivedProps;
  worlds_discovery_clicked: WorldsDiscoveryClickedProps;
  client_error: ClientErrorProps;
  daily_submission_failed: DailySubmissionFailedProps;
  play_again_clicked: PlayAgainClickedProps;
}

export type AnalyticsEventName = keyof AnalyticsEventMap;
